import { FC } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Avatar,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiUsers, FiHome, FiFileText, FiSettings } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { SearchResult } from './global-search';

interface AdminResultsProps {
  results: SearchResult[];
  onResultClick: (result: SearchResult) => void;
  selectedIndex: number;
}

export const AdminResults: FC<AdminResultsProps> = ({ results, onResultClick, selectedIndex }) => {
  const { t } = useTranslation();
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Admin-specific mock data
  const adminMockResults: SearchResult[] = [
    // Partners
    {
      id: 'admin-1',
      type: 'person',
      title: 'TechCorp Partner',
      subtitle: 'Partner',
      description: 'techcorp@partner.com',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      href: '/partners/1',
      metadata: { type: 'partner', status: 'active', credits: 1000 }
    },
    {
      id: 'admin-2',
      type: 'person',
      title: 'Digital Solutions Partner',
      subtitle: 'Partner',
      description: 'digital@partner.com',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
      href: '/partners/2',
      metadata: { type: 'partner', status: 'active', credits: 500 }
    },
    // Users
    {
      id: 'admin-3',
      type: 'person',
      title: 'Admin User',
      subtitle: 'System Administrator',
      description: 'admin@system.com',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      href: '/usuarios/3',
      metadata: { type: 'admin', status: 'active', role: 'SISADM' }
    },
    // Credit Automations
    {
      id: 'admin-4',
      type: 'assessment',
      title: 'Credit Automation - Q1 2024',
      subtitle: 'Status: Active',
      description: 'Automated credit distribution system',
      href: '/automacao-creditos/4',
      metadata: { type: 'automation', status: 'active', totalCredits: 5000 }
    },
    // Credit Sales
    {
      id: 'admin-5',
      type: 'assessment',
      title: 'Credit Sale - March 2024',
      subtitle: 'Status: Completed',
      description: 'Credit sale to TechCorp Partner',
      href: '/venda-de-creditos/5',
      metadata: { type: 'sale', status: 'completed', amount: 1000 }
    }
  ];

  const getTypeIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return <FiFileText />;
      case 'person':
        return <FiUsers />;
      case 'company':
        return <FiHome />;
      case 'group':
        return <FiSettings />;
      default:
        return <FiUsers />;
    }
  };

  const getTypeColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return 'blue';
      case 'person':
        return 'green';
      case 'company':
        return 'purple';
      case 'group':
        return 'orange';
      default:
        return 'gray';
    }
  };

  const getTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'assessment':
        return t('search.assessment');
      case 'person':
        return t('search.person');
      case 'company':
        return t('search.company');
      case 'group':
        return t('search.group');
      default:
        return type;
    }
  };

  // Combine mock results with actual results for admin
  const allResults = [...adminMockResults, ...results];

  if (allResults.length === 0) {
    return (
      <Box py={8} textAlign="center">
        <Text color="gray.500">{t('search.no_results')}</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={0} align="stretch">
      {allResults.map((result, index) => {
        const isSelected = index === selectedIndex;
        return (
          <Box
            key={result.id}
            px={4}
            py={3}
            cursor="pointer"
            bg={isSelected ? hoverBg : 'transparent'}
            _hover={{ bg: hoverBg }}
            onClick={() => onResultClick(result)}
            borderBottom={index < allResults.length - 1 ? '1px solid' : 'none'}
            borderColor={useColorModeValue('gray.100', 'gray.600')}
            borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
            borderLeftColor="blue.500"
          >
            <HStack spacing={3} align="start">
              {result.avatar ? (
                <Avatar size="sm" src={result.avatar} name={result.title} />
              ) : (
                <Box
                  p={2}
                  borderRadius="md"
                  bg={`${getTypeColor(result.type)}.100`}
                  color={`${getTypeColor(result.type)}.600`}
                >
                  {getTypeIcon(result.type)}
                </Box>
              )}
              <Box flex={1}>
                <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                  {result.title}
                </Text>
                {result.subtitle && (
                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                    {result.subtitle}
                  </Text>
                )}
                {result.description && (
                  <Text fontSize="xs" color="gray.400" noOfLines={1}>
                    {result.description}
                  </Text>
                )}
              </Box>
              <Badge size="sm" colorScheme={getTypeColor(result.type)} variant="subtle">
                {getTypeLabel(result.type)}
              </Badge>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  );
}; 